import { useEffect, useMemo, useRef, useState } from "react";
import { Row, useBlockLayout, useRowSelect, useTable } from "react-table";
import { CommonFacilityListModel, CommonFacilityListParams } from "src/api/building/building-types";
import { BaseCheckbox, BaseInputWithSearch, BaseSelect } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import { columns } from "../columns/FacilityColumns";

type Props = {
  onClick: (selectedItems: CommonFacilityListModel[]) => void;
  onClose: () => void;
  filteredFacilityList: CommonFacilityListModel[];
  selectedFacilityList: any;
};

const FACILITY_OPTIONS = [
  {
    value: "",
    label: "전체",
  },
  {
    value: "MEETING_ROOM",
    label: "회의실",
  },
  {
    value: "DESK",
    label: "좌석",
  },
  {
    value: "REFRESH_ROOM",
    label: "편의시설",
  },
];

const ReservationFacilityListModal = ({ onClick, onClose, selectedFacilityList, filteredFacilityList }: Props) => {
  const defaultParams: CommonFacilityListParams = {};

  const [params, setParams] = useState<CommonFacilityListParams>(defaultParams);
  const [selectedItems, setSelectedItems] = useState<CommonFacilityListModel[]>(selectedFacilityList);
  const [filteredFacilities, setFilteredFacilities] = useState<CommonFacilityListModel[]>(filteredFacilityList);

  const allCheckRef = useRef<HTMLInputElement>(null);

  // 초기 데이터 설정
  // console.log("totalFacilityList", totalFacilityList);
  // console.log("selectedFacilityData", selectedFacilityData);

  const enhancedFacilityList = useMemo(() => {
    return filteredFacilities.map((item) => {
      const isChecked = selectedItems.some((selected) => selected.id === item.id);
      return {
        ...item,
        checked: isChecked,
      };
    });
  }, [filteredFacilities, selectedItems]);

  useEffect(() => {
    applyFilters();
  }, [params.searchType, selectedFacilityList]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable(
    {
      columns,
      data: enhancedFacilityList,
    },
    useBlockLayout,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns: Array<any>) => [
        {
          width: 60,
          Header: ({ rows }) => {
            return (
              <BaseCheckbox
                inputRef={allCheckRef}
                checked={rows.length > 0 && rows.every((row: any) => row.original.checked)}
                id={"facility-select-all"}
                name={"facility-select-all"}
                onChange={(checked: boolean) => {
                  console.log("현재 표시된 행:", rows.length);
                  if (checked) {
                    // 현재 표시된 항목만 선택 (rows에서 가져옴)
                    const visibleItems = rows.map((row) => row.original);
                    setSelectedItems(visibleItems);
                  } else {
                    // 모든 선택 해제
                    setSelectedItems([]);
                  }
                }}
              />
            );
          },
          accessor: "checked",
          Cell: ({ row }: any) => {
            return (
              <BaseCheckbox
                id={row.id}
                name={row.id}
                checked={row.original.checked}
                onChange={(checked: boolean) => {
                  setSelectedItems((prev) => {
                    if (checked) {
                      // 항목 추가
                      return [...prev, row.original];
                    } else {
                      // 항목 제거
                      return prev.filter((item) => item.id !== row.original.id);
                    }
                  });
                }}
                // disabled={isCheckDisabled(row.original)}
              />
            );
          },
        },
        ...columns,
      ]);
    },
  );

  // 검색 필터 적용
  const applyFilters = () => {
    try {
      let filtered = [...filteredFacilityList];

      // 키워드로 필터링
      if (params.keywordList) {
        const keyword = params.keywordList.toLowerCase();
        filtered = filtered.filter((item) => {
          return item.facilityName && item.facilityName.toLowerCase().includes(keyword);
        });
      }
      setFilteredFacilities(filtered);

      // 전체선택 체크박스 상태 업데이트
      if (allCheckRef.current) {
        // 현재 필터링된 항목들 중 몇 개가 선택되었는지 확인
        const visibleItemsSelectedCount = filtered.filter((item) => selectedItems.some((selected) => selected.id === item.id)).length;

        // 보이는 항목이 모두 선택된 경우에만 전체 선택 체크박스를 체크
        allCheckRef.current.checked = filtered.length > 0 && visibleItemsSelectedCount === filtered.length;

        // 부분 선택 상태 (indeterminate) 설정 - 일부만 선택된 경우
        allCheckRef.current.indeterminate = visibleItemsSelectedCount > 0 && visibleItemsSelectedCount < filtered.length;
      }
    } catch (error) {
      console.error("필터링 오류:", error);
      setFilteredFacilities(filteredFacilityList);
    }
  };

  const tableWidth = useMemo(() => {
    let totalWidth = 0;
    headerGroups.forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => (totalWidth += Number(header?.width || 0)));
    });
    return totalWidth;
  }, [headerGroups]);

  return (
    <BaseAbstractModal isOpen={true}>
      <section className="base-abstract-modal__title flex-center-between ">
        <h1>공용공간 선택</h1>
        <div className="right-area">
          <div className="flex-center">
            <BaseInputWithSearch
              inputValue={params.keywordList}
              selectValue={params.searchType}
              stateOptions={[
                {
                  value: "FACILITY_NAME",
                  label: "공용공간명",
                },
              ]}
              onKeyUp={() => {
                applyFilters();
              }}
              onChange={(keywordList: string) => {
                setParams((prev) => ({ ...prev, keywordList }));
              }}
              setStateValue={(searchType: string) => {
                setParams((prev) => ({ ...prev, searchType }));
              }}
              onSearchClick={() => applyFilters()}
            />
          </div>
        </div>
      </section>

      <section className="base-abstract-modal__contents">
        <div className="contents-container__table px30">
          <div {...getTableProps()} className="base-table view-data-table sticky">
            <div className="header">
              {headerGroups.map((headerGroup) => (
                <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                  {headerGroup.headers.map((header) => {
                    return (
                      <div {...header.getHeaderProps()} className="base-table__th">
                        {header.render("Header")}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {rows.length > 0 ? (
              <div {...getTableBodyProps()} className="body">
                {rows.map((row, idx: number) => {
                  prepareRow(row);
                  return (
                    <div {...row.getRowProps()} className="base-table__tr">
                      {row.cells.map((cell) => {
                        return (
                          <div {...cell.getCellProps()} className="base-table__td">
                            {cell.render("Cell")}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                <div className="base-table__td w-100 text-center">
                  <div className="w-100">
                    <span>데이터가 없습니다.</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="base-abstract-modal__btn-wrap">
        <button className="base-btn color-white mr10" onClick={() => onClose()}>
          취소
        </button>
        <button
          className="base-btn"
          disabled={selectedItems.length === 0}
          onClick={() => {
            onClick(selectedItems);
            onClose();
          }}
        >
          선택
        </button>
      </section>
    </BaseAbstractModal>
  );
};

export default ReservationFacilityListModal;
